/**
 * useCustomerDashboard Hook
 * 
 * Fetches customer dashboard data including stats and recent communications.
 * Uses CRM service queries from the backend.
 * 
 * Requirements: 7.2, 7.3 - Fetch customer dashboard data
 */

import {
  useGetMyDashboardQuery,
  useListMyCommunicationsQuery,
  useCountUnreadCommunicationsQuery,
  type GetMyDashboardQuery,
  type ListMyCommunicationsQuery,
  type CommunicationFiltersInput,
  type PaginationInput,
} from '@bloomcode/graphql';

// Type aliases for query results
type DashboardData = NonNullable<GetMyDashboardQuery['getMyDashboard']>;
type CommunicationItem = ListMyCommunicationsQuery['listMyCommunications'][number];

interface UseCustomerDashboardOptions {
  communicationLimit?: number;
  skip?: boolean;
}

interface UseCustomerDashboardResult {
  dashboard: DashboardData | null;
  communications: CommunicationItem[];
  unreadCount: number;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch all customer dashboard data
 * 
 * @param options - Configuration options
 * @returns Customer dashboard data with loading and error states
 */
export function useCustomerDashboard(options: UseCustomerDashboardOptions = {}): UseCustomerDashboardResult {
  const { communicationLimit = 10, skip = false } = options;

  // Fetch dashboard stats
  const {
    data: dashboardData,
    loading: dashboardLoading,
    error: dashboardError,
    refetch: refetchDashboard,
  } = useGetMyDashboardQuery({
    skip,
    fetchPolicy: 'cache-and-network',
  });

  // Fetch recent communications
  const pagination: PaginationInput = { first: communicationLimit };
  const {
    data: communicationsData,
    loading: communicationsLoading,
    error: communicationsError,
    refetch: refetchCommunications,
  } = useListMyCommunicationsQuery({
    variables: { pagination },
    skip,
    fetchPolicy: 'cache-and-network',
  });

  // Fetch unread count
  const {
    data: unreadData,
    loading: unreadLoading,
    refetch: refetchUnread,
  } = useCountUnreadCommunicationsQuery({
    skip,
    fetchPolicy: 'cache-and-network',
  });

  const refetch = () => {
    refetchDashboard();
    refetchCommunications();
    refetchUnread();
  };

  return {
    dashboard: dashboardData?.getMyDashboard ?? null,
    communications: communicationsData?.listMyCommunications ?? [],
    unreadCount: unreadData?.countUnreadCommunications ?? 0,
    isLoading: dashboardLoading || communicationsLoading || unreadLoading,
    error: dashboardError ?? communicationsError ?? null,
    refetch,
  };
}

/**
 * Hook to fetch only customer dashboard stats
 */
export function useCustomerStats(options: { skip?: boolean } = {}) {
  const { skip = false } = options;

  const { data, loading, error, refetch } = useGetMyDashboardQuery({
    skip,
    fetchPolicy: 'cache-and-network',
  });

  return {
    stats: data?.getMyDashboard ?? null,
    isLoading: loading,
    error: error ?? null,
    refetch,
  };
}

/**
 * Hook to fetch recent communications with optional filters
 */
export function useRecentCommunications(options: {
  limit?: number;
  filters?: CommunicationFiltersInput;
  skip?: boolean;
} = {}) {
  const { limit = 10, filters, skip = false } = options;

  const pagination: PaginationInput = { first: limit };
  const { data, loading, error, refetch } = useListMyCommunicationsQuery({
    variables: { filters, pagination },
    skip,
    fetchPolicy: 'cache-and-network',
  });

  return {
    communications: data?.listMyCommunications ?? [],
    isLoading: loading,
    error: error ?? null,
    refetch,
  };
}

/**
 * Hook to fetch unread communications count
 */
export function useUnreadCount(options: { skip?: boolean } = {}) {
  const { skip = false } = options;

  const { data, loading, error, refetch } = useCountUnreadCommunicationsQuery({
    skip,
    fetchPolicy: 'cache-and-network',
    pollInterval: 60000, // Poll every minute for real-time updates
  });

  return {
    count: data?.countUnreadCommunications ?? 0,
    isLoading: loading,
    error: error ?? null,
    refetch,
  };
}

export default useCustomerDashboard;
